import React, { useState } from 'react';
import classNames from 'classnames';
import validator from 'validator';
import { useTranslation } from 'react-i18next';
import { ActionButton, ButtonType } from 'components/Atoms/ActionButton/ActionButton';
import { Icon } from 'components/Atoms/Icon/Icon';
import { ResidentCustomPublicationConsent } from 'types/publishedAssets';
import { SignupDisclaimer } from './SignupDisclaimer';
import { useNotificationSignupSubscribe } from './useNotificationSignupSubscribe';
import styles from './NotificationSignup.module.scss';

interface Props {
  buttonLabel: string;
  residentCustomPublicationIds?: string[];
  consents?: ResidentCustomPublicationConsent[];
}

export function AnonymousSignupForm({ buttonLabel, residentCustomPublicationIds, consents }: Props) {
  const { t } = useTranslation();
  const { subscribe } = useNotificationSignupSubscribe(residentCustomPublicationIds);
  const [email, setEmail] = useState('');
  const [showError, setShowError] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const validEmail = validator.isEmail(email);

  const handleSubmit = async () => {
    if (!validEmail) {
      setShowError(true);
      return;
    }
    setShowError(false);
    await subscribe(email);
    setHasSubmitted(true);
  };

  return (
    <>
      <div className={styles.inputRow}>
        <input
          type="email"
          className={classNames(styles.inputEmail, { [styles.inputError]: showError && !validEmail })}
          value={email}
          placeholder={t('notificationSignup.emailPlaceholder')}
          onChange={(event) => setEmail(event.target.value)}
          aria-label={t('notificationSignup.email')}
        />
        <ActionButton type={ButtonType.ACTION} onClick={handleSubmit} className={styles.submitButton}>
          {buttonLabel}
        </ActionButton>
      </div>
      {showError && !validEmail && (
        <div className={styles.errorMessage}>
          <Icon name="warning" /> {t('notificationSignup.requiredEmail')}
        </div>
      )}
      {hasSubmitted && (
        <div className={styles.noticeBox}>
          <Icon name="info" />
          <p>{t('notificationSignup.verifyEmailNotice')}</p>
        </div>
      )}
      <SignupDisclaimer html={t('notificationSignup.formDisclaimer')} consents={consents} />
    </>
  );
}
